import { useEffect } from "react";

/**
 * Lightweight SEO manager — updates document.title, meta description,
 * canonical, and OG/Twitter tags for the current route. No external deps.
 *
 * Also lets a page inject one JSON-LD block via the `jsonLd` field (object
 * or array). It is removed on unmount to keep the head clean when navigating.
 */
export default function useSEO({ title, description, canonical, image, type = "website", jsonLd } = {}) {
  useEffect(() => {
    const originalTitle = document.title;
    if (title) document.title = title;

    const setMeta = (attr, key, content) => {
      if (content == null) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    if (title) {
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("name", "twitter:image", image);
    }
    setMeta("property", "og:type", type);

    if (canonical) {
      setLink("canonical", canonical);
      setMeta("property", "og:url", canonical);
    }

    let ldScript = null;
    if (jsonLd) {
      ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.setAttribute("data-page-seo", "1");
      ldScript.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ldScript);
    }
    return () => {
      document.title = originalTitle;
      if (ldScript && ldScript.parentNode) ldScript.parentNode.removeChild(ldScript);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, image, type, JSON.stringify(jsonLd || null)]);
}

export const siteOrigin =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || "https://corporaco.com");

/** Build a canonical URL for a route (no query string). */
export const canonicalFor = (path = "/") => `${siteOrigin}${path.startsWith("/") ? path : "/" + path}`;

/** BreadcrumbList JSON-LD helper. crumbs = [{name, url}, ...] */
export const breadcrumbLd = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: c.url,
  })),
});
